var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useMemo } from "react";
import { Autocomplete, Box, TextField, Typography, } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTimeZoneService } from "./timeZoneService";
/** The picker's text of an option: `timezones.optionPrimary` marks the primary. */
export var optionText = function (option, primary, service) {
    return option.zone === primary
        ? service.timeZonePrimaryOptionLabel(option, service.text)
        : option.label;
};
/** The options of `zones` (every zone when absent) at `at`, and the search over them. */
export var useTimeZoneOptions = function (zones, at) {
    var service = useTimeZoneService();
    var universe = useMemo(function () { return (zones ? __spreadArray([], zones, true) : service.listTimeZones()); }, [zones, service]);
    // By value: a caller's new Date of the same instant keeps the options (and the
    // selected option's identity, which the Autocomplete compares).
    var atTime = at === null || at === void 0 ? void 0 : at.getTime();
    var options = useMemo(function () {
        return service.searchTimeZones("", universe, service.text, atTime === undefined ? undefined : new Date(atTime));
    }, [service, universe, atTime]);
    var byZone = useMemo(function () { return new Map(options.map(function (option) { return [option.zone, option]; })); }, [options]);
    var optionOf = useCallback(function (zone) {
        var _a;
        return (_a = byZone.get(zone)) !== null && _a !== void 0 ? _a : service.timeZoneOption(zone, service.text, atTime === undefined ? undefined : new Date(atTime));
    }, [byZone, service, atTime]);
    var filter = function (query) {
        return query.trim() ? service.searchTimeZones(query, universe, service.text, at) : options;
    };
    return { service: service, options: options, optionOf: optionOf, filter: filter };
};
/** One option: "(GMT+08:00) Manila · primary" over "Philippines · Philippine Standard Time". */
export var TimeZoneOptionRow = function (_a) {
    var option = _a.option, primary = _a.primary, props = _a.props;
    var service = useTimeZoneService();
    return (_jsxs(Box, __assign({ component: "li" }, props, { sx: {
            "&&": {
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                textAlign: "start",
            },
        }, children: [_jsx(Typography, { component: "span", variant: "body2", sx: { fontWeight: 500, m: 0, width: "100%", textAlign: "start" }, children: optionText(option, primary, service) }), option.detail ? (_jsx(Typography, { component: "span", variant: "caption", color: "text.secondary", sx: { m: 0, width: "100%", textAlign: "start" }, children: option.detail })) : null] })));
};
/**
 * A searchable timezone picker: type a city, country, zone name,
 * abbreviation or offset; arrows, Enter and Escape work as in any combobox.
 */
export var TimeZonePicker = function (_a) {
    var _b;
    var value = _a.value, onChange = _a.onChange, zones = _a.zones, _c = _a.additionalOptions, additionalOptions = _c === void 0 ? [] : _c, primary = _a.primary, label = _a.label, helperText = _a.helperText, error = _a.error, disabled = _a.disabled, required = _a.required, _d = _a.clearable, clearable = _d === void 0 ? false : _d, at = _a.at, placeholder = _a.placeholder, size = _a.size, sx = _a.sx;
    var t = useTranslation().t;
    var _e = useTimeZoneOptions(zones, at), service = _e.service, options = _e.options, optionOf = _e.optionOf, filter = _e.filter;
    var selected = value
        ? ((_b = additionalOptions.find(function (option) { return option.zone === value; })) !== null && _b !== void 0 ? _b : optionOf(value))
        : null;
    return (_jsx(Autocomplete, { options: __spreadArray(__spreadArray([], additionalOptions, true), options, true), value: selected, disabled: disabled, disableClearable: !clearable, size: size, fullWidth: true, sx: sx, slotProps: { listbox: { sx: { maxHeight: 280, py: 0.5 } } }, autoHighlight: true, onChange: function (_event, option) { var _a; return onChange((_a = option === null || option === void 0 ? void 0 : option.zone) !== null && _a !== void 0 ? _a : null); }, isOptionEqualToValue: function (option, current) { return option.zone === current.zone; }, getOptionLabel: function (option) { return optionText(option, primary, service); }, 
        // Autocomplete passes "" while the field shows the selected option.
        filterOptions: function (_options, _a) {
            var inputValue = _a.inputValue;
            return __spreadArray(__spreadArray([], additionalOptions.filter(function (option) {
                return option.label.toLocaleLowerCase().includes(inputValue.toLocaleLowerCase());
            }), true), filter(inputValue), true);
        }, noOptionsText: t("lifecycle.picker.noMatch"), renderOption: function (_a, option) {
            var key = _a.key, props = __rest(_a, ["key"]);
            return (_jsx(TimeZoneOptionRow, { option: option, primary: primary, props: props }, key));
        }, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { label: label, required: required, error: error, helperText: helperText, placeholder: placeholder, 
            // A placeholder names what an empty value means, so it stays visible.
            InputLabelProps: placeholder
                ? __assign(__assign({}, params.InputLabelProps), { shrink: true }) : params.InputLabelProps }))); } }));
};
